
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField, emiratesOptions } from "./BaseForm";
import { BusinessInterruptionFormData, FormProps } from "@/types/forms";

type BusinessInterruptionFormProps = FormProps<BusinessInterruptionFormData>;

const BusinessInterruptionForm = ({ onSubmit, initialData = {}, buttonText = "Continue" }: BusinessInterruptionFormProps) => {
  const [formData, setFormData] = useState<BusinessInterruptionFormData>({
    monthlyRevenue: initialData.monthlyRevenue || "",
    businessLocation: initialData.businessLocation || "",
    linkedToProperty: initialData.linkedToProperty || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.monthlyRevenue) newErrors.monthlyRevenue = "Monthly revenue is required";
    if (!formData.businessLocation) newErrors.businessLocation = "Business location is required";
    if (!formData.linkedToProperty) newErrors.linkedToProperty = "Please specify if linked to property policy";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof BusinessInterruptionFormData>(field: K, value: BusinessInterruptionFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormField label="Monthly revenue (AED)" required>
        <Input
          type="number"
          placeholder="Enter monthly revenue in AED"
          value={formData.monthlyRevenue}
          onChange={(e) => updateField('monthlyRevenue', e.target.value)}
        />
        {errors.monthlyRevenue && <p className="text-red-500 text-sm">{errors.monthlyRevenue}</p>}
      </FormField>

      <FormField label="Main business location (Emirate)" required>
        <Select value={formData.businessLocation} onValueChange={(value) => updateField('businessLocation', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select emirate" />
          </SelectTrigger>
          <SelectContent>
            {emiratesOptions.map((emirate) => (
              <SelectItem key={emirate} value={emirate}>
                {emirate}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.businessLocation && <p className="text-red-500 text-sm">{errors.businessLocation}</p>}
      </FormField>

      <FormField label="Is this linked to a Property All Risks policy?" required>
        <Select value={formData.linkedToProperty} onValueChange={(value) => updateField('linkedToProperty', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
        {errors.linkedToProperty && <p className="text-red-500 text-sm">{errors.linkedToProperty}</p>}
      </FormField>

      <Button type="submit" className="w-full">
        {buttonText}
      </Button>
    </form>
  );
};

export default BusinessInterruptionForm;
